import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import OpeningView from './view';
import { updateOpenTimes } from 'api';

const daysOfWeek = [
  { day: 0, label: 'Monday' },
  { day: 1, label: 'Tuesday' },
  { day: 2, label: 'Wednesday' },
  { day: 3, label: 'Thursday' },
  { day: 4, label: 'Friday' },
  { day: 5, label: 'Saturday' },
  { day: 6, label: 'Sunday' },
];

const OpeningContainer = ({ venueData, onVenueUpdate }) => {
  const [openingTimes, setOpeningTimes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Snackbar helper function
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Initialize opening times from venue data
  useEffect(() => {
    setIsLoading(true);
    
    const initialTimes = daysOfWeek.map(({ day }) => {
      const existingTime = venueData?.open_time?.find(time => time.day === day);
      return existingTime || { 
        day, 
        time_start: '', 
        time_end: '', 
        status: 'Closed' 
      };
    });

    if (initialTimes.length) {
      setOpeningTimes(initialTimes);
    } else {
      console.warn("No opening times found for the venue data.");
      setOpeningTimes(daysOfWeek.map(({ day }) => ({ 
        day, 
        time_start: '', 
        time_end: '', 
        status: 'Closed' 
      })));
    }
    
    setIsLoading(false);
  }, [venueData]);

  // Handle opening/closing toggle
  const handleSwitchChange = useCallback((dayIndex) => {
    setOpeningTimes(prev => {
      const updatedTimes = [...prev];
      const currentStatus = updatedTimes[dayIndex].status === 'Closed' ? 'AC' : 'Closed';

      // Set default open and close times when opened
      if (currentStatus === 'AC') {
        updatedTimes[dayIndex] = {
          ...updatedTimes[dayIndex],
          status: currentStatus,
          time_start: '09:00', // Default open time
          time_end: '18:00',   // Default close time
        };
      } else {
        updatedTimes[dayIndex] = {
          ...updatedTimes[dayIndex],
          status: currentStatus,
          time_start: '', // Reset times when closed
          time_end: '',
        };
      }

      setHasChanges(true);
      return updatedTimes;
    });
  }, []);

  // Handle time changes
  const handleTimeChange = useCallback((dayIndex, field, value) => {
    setOpeningTimes(prev => {
      const updatedTimes = [...prev];
      updatedTimes[dayIndex] = {
        ...updatedTimes[dayIndex],
        [field]: value,
        status: 'AC', // Set status to Active when time is set
      };
      
      setHasChanges(true);
      return updatedTimes;
    });
  }, []);

  // Save opening times
  const handleSave = useCallback(async () => {
    if (!venueData?.slug || !hasChanges) return;

    setIsSaving(true);

    const formattedData = openingTimes.map(({ day, time_start, time_end, status }) => ({
      day,
      time_start,
      time_end,
      status,
    }));

    try {
      await updateOpenTimes({ 
        slug: venueData.slug, 
        openingTimes: formattedData 
      });
      
      setHasChanges(false);
      showSnackbar('Opening times updated successfully', 'success');

      // Notify parent to refresh venue data
      if (onVenueUpdate) {
        onVenueUpdate();
      }
    } catch (error) {
      console.error('Failed to update opening times:', error);
      showSnackbar('Failed to update opening times: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setIsSaving(false);
    }
  }, [venueData?.slug, openingTimes, hasChanges, showSnackbar]);

  return (
    <>
      <OpeningView
        openingTimes={openingTimes}
        isLoading={isLoading}
        isSaving={isSaving}
        hasChanges={hasChanges}
        handleSwitchChange={handleSwitchChange}
        handleTimeChange={handleTimeChange}
        handleSave={handleSave}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OpeningContainer;